import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { BaseForm } from "components/BaseForm";
import EmailFooterService from "services/EmailFooters";
import EmailTemplateService from "services/EmailTemplate";
import { fetchLatestInfo } from "routes/email/emailTemplateCache";
import RenderEmailTemplate from "routes/email/forms/RenderEmailTemplate";

const PROVIDERS = [
  { id: "mailgun_sendgrid", label: "Mailgun" },
  { id: "constant_contact", label: "Constant Contact" },
  { id: "smtp", label: "SMTP" },
  { id: "manual", label: "Manual Send" },
];

const byProvider = valueFor =>
  Object.fromEntries(PROVIDERS.map(p => [p.id, valueFor(p.id)]));

const schema = z.object({
  provider_list: z.object(byProvider(() => z.boolean())),
  footer_ids: z.object(byProvider(() => z.number().nullable())),
});

const getFooterOptions = async () => {
  const footers = await EmailFooterService.getEmailFootersForFilters();
  return footers.map(({ id, name }) => ({ key: id, value: id, text: name }));
};

/** The providers the template is offered for, each with its own preview. */
const RenderEmailTemplateList = ({ template, onSaved, onCancel }) => {
  const { data: footerOptions = [] } = useQuery({
    queryKey: ["email-footer-options"],
    queryFn: getFooterOptions,
  });

  const defaultValues = useMemo(
    () => ({
      provider_list: byProvider(id => !!template.info?.provider_list?.[id]),
      footer_ids: byProvider(() => null),
    }),
    [template]
  );

  const handleSubmit = async ({ provider_list }) => {
    const info = await fetchLatestInfo(template.id);
    const saved = await EmailTemplateService.editEmailTemplate({
      id: template.id,
      info: { ...info, provider_list },
    });
    await onSaved(saved);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      {PROVIDERS.map(provider => (
        <RenderEmailTemplate
          key={provider.id}
          provider={provider}
          template={template}
          footerOptions={footerOptions}
        />
      ))}
    </BaseForm>
  );
};

export default RenderEmailTemplateList;
